import { THEME, NODE_TYPE_LABELS, LINK_TYPE_LABELS, formatCurrency } from "../utils/theme";

/**
 * DetailPanel — slide-out panel showing a selected node's details and connections.
 */
export default function DetailPanel({
  node,
  allLinks,
  allNodes,
  onClose,
  onNavigate,
  currencySymbol = "\u00a3",
}) {
  if (!node) return null;

  const connections = allLinks
    .filter((l) => {
      const sid = typeof l.source === "object" ? l.source.id : l.source;
      const tid = typeof l.target === "object" ? l.target.id : l.target;
      return sid === node.id || tid === node.id;
    })
    .map((l) => {
      const sid = typeof l.source === "object" ? l.source.id : l.source;
      const tid = typeof l.target === "object" ? l.target.id : l.target;
      const otherId = sid === node.id ? tid : sid;
      const other = allNodes.find((n) => n.id === otherId);
      return { ...l, other };
    });

  return (
    <div style={styles.root}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{
              width: 10, height: 10,
              borderRadius: node.type === "contact" ? "50%" : 2,
              background: THEME.nodeColors[node.type].fill,
              display: "inline-block",
            }} />
            <span style={{ fontSize: 16, fontWeight: 600 }}>{node.label}</span>
          </div>
          <span style={styles.typeLabel}>{NODE_TYPE_LABELS[node.type]}</span>
        </div>
        <button onClick={onClose} style={styles.close}>\u00d7</button>
      </div>

      {/* Metadata */}
      {node.role && <div style={styles.meta}>Role: {node.role}</div>}
      {node.value && (
        <div style={styles.value}>{formatCurrency(node.value, currencySymbol)}</div>
      )}
      {node.stage && (
        <div style={{
          ...styles.badge,
          background: (THEME.stageColors[node.stage] || "#666") + "22",
          color: THEME.stageColors[node.stage] || "#aaa",
        }}>
          {node.stage}
        </div>
      )}

      {/* Navigate button (optional) */}
      {onNavigate && (
        <button onClick={() => onNavigate(node)} style={styles.navBtn}>
          Open in Insights \u2192
        </button>
      )}

      {/* Connections list */}
      <div style={styles.connSection}>
        <div style={styles.connLabel}>Connections ({connections.length})</div>
        {connections.length === 0 && (
          <div style={{ fontSize: 11.5, color: THEME.textDim }}>No connections</div>
        )}
        {connections.map((conn, i) => (
          <div
            key={i}
            style={{
              ...styles.connRow,
              borderBottom: i < connections.length - 1 ? `1px solid ${THEME.border}` : "none",
              cursor: onNavigate ? "pointer" : "default",
            }}
            onClick={() => onNavigate?.(conn.other)}
          >
            <span style={{
              width: 6, height: 6,
              borderRadius: conn.other?.type === "contact" ? "50%" : 1.5,
              background: THEME.nodeColors[conn.other?.type]?.fill || "#666",
              display: "inline-block", flexShrink: 0,
            }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, fontWeight: 500 }}>{conn.other?.label}</div>
              <div style={{ fontSize: 10, color: THEME.textDim }}>
                {conn.label} \u00b7 {LINK_TYPE_LABELS[conn.type]?.split(" \u2192 ").pop() || conn.type}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const styles = {
  root: {
    position: "absolute", right: 16, top: 16, width: 300,
    background: THEME.surface, border: `1px solid ${THEME.borderLight}`,
    borderRadius: 12, padding: "18px 20px",
    boxShadow: "0 12px 48px rgba(0,0,0,0.4)", zIndex: 90,
    maxHeight: "calc(100% - 32px)", overflowY: "auto",
    fontFamily: "inherit",
  },
  header: {
    display: "flex", justifyContent: "space-between",
    alignItems: "flex-start", marginBottom: 12,
  },
  typeLabel: {
    fontSize: 10.5, color: THEME.textSecondary,
    textTransform: "uppercase", letterSpacing: "0.06em",
  },
  close: {
    background: "none", border: "none",
    color: THEME.textSecondary, cursor: "pointer",
    fontSize: 18, padding: 0, lineHeight: 1,
  },
  meta: { fontSize: 12.5, color: THEME.textSecondary, marginBottom: 8 },
  value: {
    fontSize: 13, color: THEME.accent,
    fontFamily: "monospace", marginBottom: 8,
  },
  badge: {
    display: "inline-block", padding: "3px 10px",
    borderRadius: 4, fontSize: 11, fontWeight: 500,
    textTransform: "capitalize", marginBottom: 12,
  },
  navBtn: {
    display: "block", width: "100%", padding: "8px 0",
    background: THEME.accent + "18", color: THEME.accent,
    border: `1px solid ${THEME.accent}44`, borderRadius: 6,
    fontSize: 12, fontWeight: 500, cursor: "pointer",
    marginBottom: 12, fontFamily: "inherit",
  },
  connSection: {
    borderTop: `1px solid ${THEME.border}`,
    paddingTop: 12, marginTop: 4,
  },
  connLabel: {
    fontSize: 10, fontWeight: 600,
    textTransform: "uppercase", letterSpacing: "0.08em",
    color: THEME.textDim, marginBottom: 8,
  },
  connRow: {
    display: "flex", alignItems: "center", gap: 8,
    padding: "6px 0",
  },
};
